import { Button, Flex, Input, Typography } from 'antd'
import {
  AlertTriangle,
  Flag,
  Send,
  Sparkles,
  TrendingUp,
  type LucideIcon
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  answerQuestion,
  computeDigest,
  computeSummary,
  SUGGESTED_PROMPTS
} from './activityData'
import { useStyles } from './styles'
import type {
  BusinessEvent,
  ChatMessage,
  DigestItem,
  InsightPeriod
} from './types'

const { Text } = Typography

let msgCounter = 0
const nextMsgId = () => `msg-${msgCounter++}`

const DIGEST_ICON: Record<
  DigestItem['tone'],
  { icon: LucideIcon; color: string }
> = {
  positive: { icon: TrendingUp, color: 'var(--ant-color-success)' },
  warning: { icon: AlertTriangle, color: 'var(--ant-color-warning)' },
  neutral: { icon: Flag, color: 'var(--ant-color-info)' }
}

type Props = { events: BusinessEvent[]; period: InsightPeriod }

export const AiInsightsTab = ({ events, period }: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)

  const summary = useMemo(
    () => computeSummary(events, period),
    [events, period]
  )
  const digest = useMemo(() => computeDigest(events, period), [events, period])

  useEffect(() => {
    const el = threadRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, thinking])

  const ask = (question: string) => {
    const q = question.trim()
    if (!q || thinking) return
    setInput('')
    setMessages((m) => [...m, { id: nextMsgId(), role: 'user', text: q }])
    setThinking(true)
    window.setTimeout(() => {
      setMessages((m) => [
        ...m,
        { id: nextMsgId(), role: 'assistant', text: answerQuestion(events, q) }
      ])
      setThinking(false)
    }, 650)
  }

  return (
    <div className={styles.insights}>
      <div>
        <div className={styles.sectionLabel}>
          {t('workspaceOverview.companyGraph.highlights')}
        </div>
        <div className={styles.digest}>
          {digest.map((item) => {
            const meta = DIGEST_ICON[item.tone]
            const Icon = meta.icon

            return (
              <div key={item.id} className={styles.digestItem}>
                <span
                  className={styles.digestIcon}
                  style={{ color: meta.color }}
                >
                  <Icon size={15} />
                </span>
                <span>
                  {item.text}
                  {item.cite ? (
                    <Text type="secondary" className={styles.digestCite}>
                      · {item.cite}
                    </Text>
                  ) : null}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <div className={styles.sectionLabel}>
          {t('workspaceOverview.companyGraph.insights.latestChanges')}
        </div>
        <div className={styles.insightSummary}>
          {summary.map((stat) => (
            <div key={stat.key} className={styles.insightStat}>
              <Text
                className={styles.insightStatValue}
                style={{ color: stat.color }}
              >
                {stat.value}
              </Text>
              <div className={styles.insightStatLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div ref={threadRef} className={styles.chatThread}>
        {messages.length === 0 ? (
          <div className={cx(styles.chatBubble, styles.chatAssistant)}>
            <Flex align="center" gap={6}>
              <Sparkles size={13} />
              {t('workspaceOverview.companyGraph.insights.greeting')}
            </Flex>
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={cx(
                styles.chatBubble,
                m.role === 'user' ? styles.chatUser : styles.chatAssistant
              )}
            >
              {m.text}
            </div>
          ))
        )}
        {thinking ? (
          <div className={cx(styles.chatBubble, styles.chatAssistant)}>
            {t('workspaceOverview.companyGraph.insights.thinking')}
          </div>
        ) : null}
      </div>

      <div className={styles.chatPrompts}>
        {SUGGESTED_PROMPTS.map((p) => (
          <span
            key={p}
            className={styles.promptChip}
            role="button"
            tabIndex={0}
            onClick={() => ask(p)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') ask(p)
            }}
          >
            {p}
          </span>
        ))}
      </div>

      <Flex gap={8}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPressEnter={() => ask(input)}
          placeholder={t(
            'workspaceOverview.companyGraph.insights.askPlaceholder'
          )}
          disabled={thinking}
        />
        <Button
          type="primary"
          icon={<Send size={14} />}
          onClick={() => ask(input)}
          disabled={thinking || !input.trim()}
        >
          {t('workspaceOverview.companyGraph.insights.send')}
        </Button>
      </Flex>
    </div>
  )
}
